<template>
  <div
    class="subject-form__prep-factor-wrap"
    :class="[wrapClass, {'subject-form__prep-factor-wrap--binary': isBinaryPrepTask}]">
    <template v-if="isBinaryPrepTask">
      <div class="d-label-and-toggle" @click.stop="onPrepToggle(!isPrepExtraOn)">
        <label style="margin-bottom: 0" v-tippy="toggleTippyOptions">{{ labels.prep_task_extra_toggle }}</label>
        <AppToggle :propOn="isPrepExtraOn" :allowChanges="false"></AppToggle>
      </div>
    </template>
    <template v-else>
      <label style="margin-bottom: 6px;">{{ labels.forb_faktor }}</label>
      <div class="input-group--flex subject-form__prep-factor-inner">
        <template v-if="!isDanishPreparationFactorEditing">
          <div class="flex-1">
            <span class="subject-form__prep-factor-display">{{ formattedDanishPreparationFactor }}</span>
          </div>
          <div class="input-group--flex__setting-icon">
            <div class="icon__bg-circle" @click="openDanishPreparationFactorEdit" role="button" tabindex="0">
              <span class="icon-wrench"></span>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex-1">
            <input
              type="number"
              step="0.01"
              min="-1"
              max="1"
              :class="inputClass"
              :value="numericEditDraft"
              ref="inputDanishPreparationFactor"
              v-tippy="numericTippyOptions"
              @input="onDanishPreparationFactorInput"
              @blur="onDanishPreparationFactorBlur"
              @focus="selectAllOnFocus">
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import AppToggle from '../common/AppToggle.vue'
import {isDanishPreparationBinaryPrepTaskSubjectType} from '../../utils/dkPreparationFactorUi'
import {
  parseNumericPreparationFactor,
  effectivePreparationFactor,
  clampDanishPreparationFactor
} from '../../utils/preparationFactor'

export default {
  name: 'DkPreparationFactorControl',
  components: {AppToggle},
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      default: null
    },
    subjectType: {
      type: String,
      required: true
    },
    schoolPrepFactor: {
      default: null
    },
    labels: {
      type: Object,
      required: true
    },
    inputClass: {
      type: String,
      default: 'do-input'
    },
    wrapClass: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isDanishPreparationFactorEditing: false,
      numericEditDraft: ''
    }
  },
  computed: {
    isBinaryPrepTask () {
      return isDanishPreparationBinaryPrepTaskSubjectType(this.subjectType)
    },
    schoolPrepFactorNumeric () {
      return parseNumericPreparationFactor(this.schoolPrepFactor)
    },
    isPrepExtraOn () {
      return parseNumericPreparationFactor(this.value) === 1
    },
    effectiveDanishPreparationFactorNumeric () {
      const eff = effectivePreparationFactor(this.value, this.schoolPrepFactorNumeric)
      if (eff === null || isNaN(eff)) {
        return 0
      }
      return eff
    },
    formattedDanishPreparationFactor () {
      const n = this.effectiveDanishPreparationFactorNumeric
      if (typeof n !== 'number' || isNaN(n)) {
        return '0'
      }
      return String(Number(n.toFixed(4)))
    },
    numericTippyOptions () {
      return {
        content: this.labels.forb_faktor_range_hint,
        arrow: true,
        animateFill: false,
        animation: 'shift-away',
        zIndex: '999999992',
        boundary: 'window',
        trigger: 'mouseenter focus',
        maxWidth: 280
      }
    },
    toggleTippyOptions () {
      return {
        content: this.labels.prep_task_extra_toggle_hint,
        arrow: true,
        animateFill: false,
        animation: 'shift-away',
        zIndex: '999999992',
        boundary: 'window',
        trigger: 'mouseenter focus',
        maxWidth: 320
      }
    }
  },
  watch: {
    subjectType () {
      this.isDanishPreparationFactorEditing = false
      this.numericEditDraft = ''
    },
    isBinaryPrepTask (isBinary) {
      if (isBinary) {
        this.isDanishPreparationFactorEditing = false
        this.numericEditDraft = ''
      }
    }
  },
  methods: {
    valueToDraft (v) {
      if (v === null || v === undefined || v === '') {
        return ''
      }
      const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
      if (isNaN(n)) {
        return ''
      }
      return String(n)
    },
    onPrepToggle (isOn) {
      this.$emit('input', isOn ? 1 : null)
    },
    openDanishPreparationFactorEdit () {
      this.isDanishPreparationFactorEditing = true
      this.numericEditDraft = this.valueToDraft(this.value)
      this.$nextTick(_ => {
        const el = this.$refs.inputDanishPreparationFactor
        if (el) {
          el.focus()
        }
      })
    },
    onDanishPreparationFactorInput (e) {
      const raw = e.target.value
      this.numericEditDraft = raw
      if (raw === '' || raw === '-' || raw === '.' || raw === ',' || raw === '-.') {
        return
      }
      const n = parseFloat(String(raw).replace(',', '.'))
      if (isNaN(n)) {
        return
      }
      const c = n < -1 ? -1 : (n > 1 ? 1 : n)
      if (c !== n) {
        this.$emit('input', c)
        this.numericEditDraft = String(c)
      } else {
        this.$emit('input', n)
      }
    },
    onDanishPreparationFactorBlur () {
      const n = parseFloat(String(this.numericEditDraft).replace(',', '.'))
      const parsed = this.numericEditDraft === '' || isNaN(n) ? null : n
      const clamped = clampDanishPreparationFactor(parsed)
      this.$emit('input', clamped)
      this.numericEditDraft = this.valueToDraft(clamped)
      this.isDanishPreparationFactorEditing = false
    },
    selectAllOnFocus (evt) {
      setTimeout(_ => {
        evt.target.select()
      }, 0)
    }
  }
}
</script>

<style scoped>
.subject-form__prep-factor-wrap {
  min-width: 0;
  max-width: 200px;
}

.subject-form__prep-factor-wrap--binary {
  max-width: min(100%, 380px);
}

.subject-form__prep-factor-wrap.dk-prep-factor-full-width {
  max-width: none;
  width: 100%;
}

.subject-form__prep-factor-inner.input-group--flex {
  align-items: stretch;
  display: inline-flex;
}

.subject-form__prep-factor-display {
  display: block;
  width: 100%;
  box-sizing: border-box;
  line-height: 1.25;
  min-height: 38px;
  padding: 8px 0 0 10px;
  cursor: default;
  user-select: none;
}

.subject-form__prep-factor-wrap .input-group--flex__setting-icon {
  opacity: 0.25;
  transition: opacity 0.15s ease;
}

.subject-form__prep-factor-wrap:hover .input-group--flex__setting-icon,
.subject-form__prep-factor-wrap:focus-within .input-group--flex__setting-icon {
  opacity: 1;
}

.d-label-and-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
}

.d-label-and-toggle label {
  cursor: pointer;
}

.d-label-and-toggle:hover {
  background: aliceblue;
}
</style>



// WEBPACK FOOTER //
// DkPreparationFactorControl.vue?28c0dce3